import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useUpdateTotalNotification } from '../../utils/useUpdateTotalNotification';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import FileUpload from '../../components/FileUpload';
import DisplayDateTime from '../../components/DisplayDateTime';
import LoadingSpinner from '../../components/LoadingSpinner';
import DownloadRemoveLink from '../../components/DownloadRemoveLink';

// TYPES
import {
	IncidentArchiveAcknowledgeOnBehalfRecipient,
	IncidentArchiveAcknowledgeOnBehalfCommand
} from '../../api/models';
import { closeModalType } from '../../components/GlobalModal';

interface AcceptModalTypes {
	recipientId?: string;
	getIncidentReportsDetailsDataArchive: () => Promise<void>;
	closeModal?: closeModalType;
}

const AcceptModal: React.FC<AcceptModalTypes> = ({ recipientId, closeModal, getIncidentReportsDetailsDataArchive }) => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const [disabled, setDiasabled] = React.useState<boolean>(false);

	// Set up data state and initial data
	const initialData = {
		recipientId: '',
		email: '',
		acceptor: '',
		acknowledged: '',
		acknowledgementFileName: '',
		acknowledgementFileUrl: ''
	};

	const [modalDetail, setModalDetail] = React.useState<IncidentArchiveAcknowledgeOnBehalfRecipient>(initialData);

	const getAcceptModalDetailArchive = async () => {
		if (recipientId) {
			setIsLoading(true);

			const response = await apibridge.getAcceptModalDetailArchive(recipientId);

			if (response.isError) {
				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);

				setIsLoading(false);
			} else {
				setIsLoading(false);

				setDiasabled(true);

				setModalDetail(response.result);
			}
		}
		setIsLoading(false);
	};

	React.useEffect(() => {
		getAcceptModalDetailArchive();
	}, []);

	const { email, acceptor, acknowledged, acknowledgementFileName, acknowledgementFileUrl } = modalDetail;

	const acceptAcknowledgementArchive = async () => {
		setIsLoading(true);

		const data: IncidentArchiveAcknowledgeOnBehalfCommand = {
			recipientId: recipientId,
			fileName: acknowledgementFileName || ''
		};

		const response = await apibridge.acceptAcknowledgementArchive(data);

		if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);

			setIsLoading(false);
		} else {
			setIsLoading(false);

			if (closeModal) {
				closeModal();
			}

			getIncidentReportsDetailsDataArchive();

			useUpdateTotalNotification();
		}
	};

	const fileUploadHandler = (value: string) => {
		setModalDetail((prev) => ({ ...prev, acknowledgementFileName: value }));

		setDiasabled(false);
	};

	const submitHandler = () => {
		acceptAcknowledgementArchive();
	};

	return (
		<div className="general-form mtop-0">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<p className="title-purple-text">{email}</p>

								<br />

								{acceptor && acknowledged ? (
									<>
										<div className="form-group">
											<label htmlFor="createAttachmentsInput" className="label">
												Accepted by
											</label>

											<div className="form-field">
												{acceptor}, <DisplayDateTime>{acknowledged}</DisplayDateTime>
											</div>
										</div>
									</>
								) : (
									<>
										<p>
											You may only accept an incident report on behalf of a recipient if you upload a copy of the report
											bearing their signature.
										</p>

										<br />
									</>
								)}

								{acceptor && acknowledged && acknowledgementFileName !== '' ? (
									<div className="form-group">
										<label htmlFor="createAttachmentsInput" className="label">
											Document uploaded
										</label>
										<DownloadRemoveLink
											onChange={() => setModalDetail((prev) => ({ ...prev, acknowledgementFileName: '' }))}
											files={[
												{
													id: acknowledgementFileUrl,
													displayFileName: 'Acknowledge Document',
													link: acknowledgementFileUrl
												}
											]}
										/>
									</div>
								) : (
									<div className="form-group">
										<label htmlFor="createAttachmentsInput" className="label">
											Upload a signed copy of the report
										</label>

										<div className="form-field mbtom-14">
											<FileUpload
												name="Attachments"
												maxSizeMb={5}
												multiple={false}
												onChange={(val: any) => fileUploadHandler(val)}
											/>
										</div>
									</div>
								)}

								<div className="form-button-group">
									<Button disabled={disabled} className="btn-primary w-100" onClick={submitHandler}>
										Accept
									</Button>
								</div>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default AcceptModal;
